import axios from 'axios';
import { httpApi } from './http';
import type { CommunityPost, CommunityListParams, CommunityListResult, Comment, CommentListParams, CommentListResult } from '../types/community';

const api = httpApi;

const pickArray = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.posts)) return payload.posts;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  return [];
};

const pickNumber = (payload: any, keys: string[], fallback: number) => {
  for (const k of keys) {
    const v = payload?.[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return fallback;
};

export class CommunityService {
  static async listPosts(params: CommunityListParams = {}): Promise<CommunityListResult> {
    try {
      const response = await api.post<any>('/community', {
        status: params.status ?? 'approved',
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        sort: params.sort ?? '-createdAt',
        ...(params.search ? { search: params.search } : {}),
        ...(params.userId ? { userId: params.userId } : {}),
        ...(params.cardId ? { cardId: params.cardId } : {}),
        includeDeleted: params.includeDeleted ?? false,
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as CommunityPost[];
      const total = pickNumber(payload, ['total', 'count'], items.length);
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);

      return { items, total, page, pages };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch community posts');
    }
  }

  static async getPostById(id: string): Promise<CommunityPost> {
    try {
      const response = await api.get<any>(`/community/${encodeURIComponent(id)}`);
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const post = (payload.data || payload.post || payload) as CommunityPost;
      if (!post || typeof post !== 'object') throw new Error('Invalid post payload');
      return post;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch community post');
    }
  }

  static async approvePost(id: string, approvedBy: string): Promise<CommunityPost> {
    try {
      const response = await api.post<any>(`/community/${encodeURIComponent(id)}/approve`, { approvedBy });
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const post = (payload.data || payload.post || payload) as CommunityPost;
      return post;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to approve community post');
    }
  }

  static async hardDeletePost(id: string): Promise<{ success?: boolean; message?: string; [key: string]: any }> {
    try {
      const response = await api.delete<any>(`/community/${encodeURIComponent(id)}/hard`);
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }
      return payload;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to hard delete community post');
    }
  }
}

export class CommentService {
  static async listComments(params: CommentListParams = {}): Promise<CommentListResult> {
    try {
      const response = await api.post<any>('/comments', {
        status: params.status ?? 'approved',
        limit: params.limit ?? 20,
        page: params.page ?? 1,
        sort: params.sort ?? '-createdAt',
        ...(params.search ? { search: params.search } : {}),
        ...(params.userId ? { userId: params.userId } : {}),
        ...(params.communityId ? { communityId: params.communityId } : {}),
        includeDeleted: params.includeDeleted ?? false,
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const items = pickArray(payload) as Comment[];
      const total = pickNumber(payload, ['total', 'count'], items.length);
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);

      return { items, total, page, pages };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch comments');
    }
  }

  static async getCommentById(id: string): Promise<Comment> {
    try {
      const response = await api.get<any>(`/comments/${encodeURIComponent(id)}`);
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const comment = (payload.data || payload.comment || payload) as Comment;
      if (!comment || typeof comment !== 'object') throw new Error('Invalid comment payload');
      return comment;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to fetch comment');
    }
  }

  static async approveComment(id: string): Promise<Comment> {
    try {
      const response = await api.post<any>(`/comments/${encodeURIComponent(id)}/approve`, {});
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }

      const comment = (payload.data || payload.comment || payload) as Comment;
      return comment;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to approve comment');
    }
  }

  static async hardDeleteComment(id: string): Promise<{ success?: boolean; message?: string; [key: string]: any }> {
    try {
      const response = await api.delete<any>(`/comments/${encodeURIComponent(id)}/hard`);
      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) {
        throw new Error(payload.error || payload.message || 'API request failed');
      }
      return payload;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
        if (apiError) throw new Error(apiError);
        if (status) throw new Error(`Server error: ${status}`);
        if (error.request) throw new Error('Network error: Unable to connect to server');
      }
      throw new Error('Failed to hard delete comment');
    }
  }
}
